import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { ICreateDto } from "@/lib/utility/data/create-dto.interface";
import { IUpdateDto } from "@/lib/utility/data/update-dto.interface";
import { $t } from "@/lib/utility/t";
import customFieldService from "@/services/mrfiktiv/services/customFieldService";
import {
  MrfiktivCreateCustomFieldDtoGen,
  MrfiktivCustomFieldConfigurationViewModelGen,
  MrfiktivCustomFieldViewModelGen,
  MrfiktivUpdateCustomFieldDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { CustomFieldDataAccessLayer } from "@/store/modules/access-layers/custom-field.access-layer";
import { CustomFieldEnum, customFieldIconTypeMap } from "@/store/modules/custom-field.store";
import Vue from "vue";
import VueRouter from "vue-router";

@IsFilterable
export class CustomFieldBase
  implements MrfiktivCustomFieldViewModelGen, ICreateDto<ICustomField>, IUpdateDto<ICustomField> {
  id: string;

  partnerId: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.customField.name"
  })
  name: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.customField.key"
  })
  key: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.customField.label"
  })
  label: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.customField.hint"
  })
  hint?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.customField.placeholder"
  })
  placeholder?: string;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.customField.type",
    config: {
      items: Object.values(CustomFieldEnum).map(e => ({
        text: $t(`enums.CustomFieldEnum.${e}`),
        value: e
      })),
      itemValue: "value"
    }
  })
  type: CustomFieldEnum;

  configuration?: MrfiktivCustomFieldConfigurationViewModelGen;

  loading = false;

  get icon() {
    return customFieldIconTypeMap.get(this.type);
  }

  get titleReadable() {
    return this.name;
  }

  get requiresConfiguration() {
    return [CustomFieldEnum.SINGLE_SELECT, CustomFieldEnum.MULTI_SELECT].includes(this.type);
  }

  constructor(customField?: Partial<CustomFieldBase | MrfiktivCustomFieldViewModelGen>) {
    this.id = customField?.id ?? "";
    this.partnerId = customField?.partnerId ?? "";
    this.name = customField?.name ?? "";
    this.key = customField?.key ?? "";
    this.label = customField?.label ?? "";
    this.hint = customField?.hint;
    this.placeholder = customField?.placeholder;
    this.type = (customField?.type ?? CustomFieldEnum.TEXT) as CustomFieldEnum;
    this.configuration = customField?.configuration;
  }

  async fetch(): Promise<this> {
    this.loading = true;

    try {
      const res = await customFieldService.getOne(this.partnerId, this.id);
      this.map(res);
      CustomFieldDataAccessLayer.set(this);
    } catch (e) {
      Vue.$log.error(e);
    } finally {
      this.loading = false;
    }

    return this;
  }

  map(customField?: MrfiktivCustomFieldViewModelGen) {
    if (!customField) return;
    this.id = customField?.id ?? "";
    this.partnerId = customField?.partnerId ?? "";
    this.name = customField?.name ?? "";
    this.key = customField?.key ?? "";
    this.label = customField?.label ?? "";
    this.hint = customField?.hint;
    this.placeholder = customField?.placeholder;
    this.type = (customField?.type ?? CustomFieldEnum.TEXT) as CustomFieldEnum;
    this.configuration = customField?.configuration;
  }

  async create() {
    const data: MrfiktivCreateCustomFieldDtoGen = {
      name: this.name,
      key: this.key,
      label: this.label,
      hint: this.hint || "",
      placeholder: this.placeholder || undefined,
      type: this.type,
      configuration: this.requiresConfiguration ? this.configuration : undefined
    };
    const res = await customFieldService.create(this.partnerId, data);

    this.map(res);
    CustomFieldDataAccessLayer.set(this);

    return this;
  }

  async delete() {
    const res = await customFieldService.delete(this.partnerId, this.id);
    this.map(res);
    CustomFieldDataAccessLayer.delete(this);
  }

  async update() {
    const data: MrfiktivUpdateCustomFieldDtoGen = {
      name: this.name,
      key: this.key,
      label: this.label,
      hint: this.hint || undefined,
      placeholder: this.placeholder || undefined,
      type: this.type,
      configuration: this.requiresConfiguration ? this.configuration : undefined
    };
    const res = await customFieldService.update(this.partnerId, this.id, data);
    this.map(res);
    CustomFieldDataAccessLayer.set(this);

    return this;
  }

  async updatePartial(dto: MrfiktivUpdateCustomFieldDtoGen) {
    const res = await customFieldService.update(this.partnerId, this.id, dto);
    this.map(res);
    CustomFieldDataAccessLayer.set(this);

    return this;
  }

  get isUpdateable() {
    return true;
  }

  goTo(router: VueRouter) {
    return {
      table: () =>
        router.push({
          name: "CustomFieldList",
          params: { partnerId: this.partnerId }
        }),
      detail: () =>
        router.push({
          name: "CustomFieldDetailView",
          params: { partnerId: this.partnerId, customFieldId: this.id }
        })
    };
  }
}

type ICustomField = CustomFieldBase;
const CustomField = Filter.createForClass(CustomFieldBase);

export { ICustomField, CustomField };
